import { Inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { DismissOcrolusAlert, GetOcrolusActivity, SendToOcrolus, SetOcrolusAlert } from './ocrolus.actions';
import { OcrolusService } from 'app/services/documents/ocrolus.service';
import { catchError, of, tap } from 'rxjs';
import { Activity } from 'app/modules/activity/models/activity/activity.model';
import { Alert } from 'app/models/dto/alert.model';
import { Document } from 'app/models/dto/document.model';
import _ from 'lodash';
import { UpdateDocumentAlert } from '../documents/document.actions';
import { RollbarService } from 'app/rollbar';
import Rollbar from 'rollbar';

export class OcrolusStateModel {
    sending: boolean;
    alert: Alert | null;
}

export const MESSAGES = {
    SUBMITTED: 'Documents have been submitted and are waiting for analysis',
    WAITING: 'Documents are still syncing with Ocrolus.  Please continue to be patient while the data is summarized.',
    SUCCESS: 'The document analysis has been completed',
    ERRORS: 'The document analysis has completed. Please review the documents below for errors.',
    UNKNOWN: 'There was an issue with the document analysis',
};

@State<OcrolusStateModel>({
  name: 'Ocrolus',
  defaults: {
    sending: false,
    alert: null
  },
})

@Injectable()
export class OcrolusState {

    @Selector()
    static ocrolusAlert(state: OcrolusStateModel) {
      return state.alert;
    }

    @Selector()
    static sending(state: OcrolusStateModel) {
      return state.sending;
    }

    constructor(
        public ocrolusService: OcrolusService,
        public store: Store,
        @Inject(RollbarService) private rollbar: Rollbar
    ) {}

    @Action(SendToOcrolus)
    sendToOcrolus( { patchState }: StateContext<OcrolusStateModel>, { borrowerId, docIds, append }: SendToOcrolus ) {
        patchState({ sending: true });
        return this.ocrolusService.sendToOcrolus(borrowerId, docIds, append).pipe(
            catchError((error) => {
                const errorText = error.message ?? 'SendToOcrolus() Error';
                this.rollbar.error(errorText, error);
                patchState({sending: false});
                this.store.dispatch(new UpdateDocumentAlert({
                    level: 'error',
                    message: 'There was an error submitted the selected document(s) to Ocrolus, please try again.'
                }));
                return of();
            }),
            tap((data) => {
                patchState({sending: false});
            })
          );
    }

    @Action(GetOcrolusActivity)
    getOcrolusActivity( { }: StateContext<OcrolusStateModel>, { borrowerId, documents }: GetOcrolusActivity ) {
        return this.ocrolusService.getOcrolusActivity(borrowerId).pipe(
            catchError((error) => {
                const errorText = error.message ?? 'GetOcrolusActivity() Error';
                this.rollbar.error(errorText, error);
                return of();
            }),
            tap((activity: Activity[]) => {
                if (!!activity[0]) {
                    const alert: Alert | null = this.getOcrolusAlertLevel(activity[0], documents, borrowerId);
                    if (!!alert) {
                        this.store.dispatch(new SetOcrolusAlert(alert));
                    }
                } else {
                    return of();
                }
            })
        );
    }

    @Action(SetOcrolusAlert)
    setOcrolusAlert({ patchState }: StateContext<OcrolusStateModel>, { alert }: SetOcrolusAlert) {
        patchState({ alert });
    }

    @Action(DismissOcrolusAlert)
    dismissOcrolusAlert({ patchState }: StateContext<OcrolusStateModel>, { borrowerId }: DismissOcrolusAlert) {
        const dismissedAlerts = this.getDismissedAlerts();
        dismissedAlerts[borrowerId] = Date.now();
        // clean up as we go
        for (const key in dismissedAlerts) {
            if (this.twoWeeksOld(dismissedAlerts[key])) {
                delete dismissedAlerts[key];
            }
        }
        localStorage.setItem('ocrolusAlertsDismissed', JSON.stringify(dismissedAlerts));
        patchState({ alert: null });
    }

    private getOcrolusAlertLevel(activity: Activity, documents: Document[], borrowerId: number): Alert | null {

        if (this.isDismissed(activity.createdTimestamp * 1000, borrowerId)) {
            return null;
        }

        documents = documents.filter(document => !!document.ocrolusStatus);

        if (activity.type === 'ocrolusSucceeded') {
            const errored = documents.filter(document => !!document.error);

            if (errored.length) {
                return {
                    level: 'warning',
                    message: MESSAGES.ERRORS
                };
            }

            return {
                level: 'success',
                message: _.get(activity, 'meta.message') || MESSAGES.SUCCESS
            };
        }

        if (activity.type === 'ocrolusSubmitted') {
            const pending = documents.filter(document => ['VERIFYING', 'QUEUED'].includes(document.ocrolusStatus));

            if (pending.length !== documents.length) {
                return {
                    level: 'info',
                    message: MESSAGES.WAITING
                };
            }

            return {
                level: 'info',
                message: MESSAGES.SUBMITTED
            };
        }

        // do not show alert banner if activity type is one of the ocrolus doc verification types
        if ([
            'ocrolusDocumentCategoryUpdated',
            'ocrolusDocumentCategoryVerified',
            'ocrolusDocumentRejected',
            'ocrolusDocumentCategoryUnknown'
        ].includes(activity.type)) {
            return null;
        }

        return {
            level: 'error',
            message: MESSAGES.UNKNOWN
        };
    }

    private isDismissed(timestamp: number, borrowerId: number) {
        return (
            this.twoWeeksOld(timestamp)
            || timestamp <= this.lastDismissed(borrowerId)
        );
    }

    private lastDismissed(borrowerId: number): number {
        return this.getDismissedAlerts()[borrowerId] || 0;
    }

    private getDismissedAlerts(): { [key: number]: number } {
        return JSON.parse(localStorage.getItem('ocrolusAlertsDismissed') || '{}');
    }

    private twoWeeksOld(timestamp) {
        return timestamp <= Date.now() - (1000 * 60 * 60 * 24 * 14);
    }

}
